// import { Button, Checkbox, Chip, Input } from "@nextui-org/react";
import { LabelControl, MapControl } from "doshx_controls_web";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { add_user_address } from "../../../api/calls/user/add_user_address";
import { setMainModal } from "../../../reducers/slices/main";
import { AddressSearch } from "../address-search";
import { isNumberValue, validateForm } from "../../../helpers/validateForm";
import { buildAddressObject } from "../helpers/address";
import { Button, Checkbox, Input } from "@heroui/react";
import { Icon } from "@iconify/react";
//If no url is provided, default to dev client url. This is needed for the address search to work in development and production environments.
const CLIENT_API_URL = process.env.LETS_TRADE_CLIENT_URL ?? "https://lets-trade-client-dev.letstrade.global";
var TypeEnum;
(function (TypeEnum) {
    TypeEnum["home"] = "HOME";
    TypeEnum["office"] = "OFFICE";
    TypeEnum["other"] = "OTHER";
})(TypeEnum || (TypeEnum = {}));
const ADDRESS_TYPES = [
    { label: TypeEnum.home, icon: "hugeicons:home-03" },
    { label: TypeEnum.office, icon: "hugeicons:briefcase-01" },
    { label: TypeEnum.other, icon: "hugeicons:location-03" },
];
export const NewAddress = (props) => {
    const dispatch = useDispatch();
    const { onComplete, addressToEdit } = props;
    const [addressType, setAddressType] = useState("");
    const [addressLabel, setAddressLabel] = useState("");
    const [instructions, setInstructions] = useState("");
    const [suiteFloor, setSuiteFloor] = useState("");
    const [unitNumber, setUnitNumber] = useState("");
    const [houseNumber, setHouseNumber] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [buildingName, setBuildingName] = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [placeId, setPlaceId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [newAddress, setNewAddress] = useState(null);
    const [coordinates, setCoordinates] = useState({
        lat: -26.0103375,
        lng: 27.9818155,
    });
    const { user } = useSelector((state) => state.userState);
    const formToValidate = (addressType) => {
        // Base validation - always require type and full address
        const baseValidation = {
            label: addressType,
            full_address: newAddress?.fullAddress ?? "",
        };
        if (!addressType) {
            return { label: addressType };
        }
        switch (addressType) {
            case TypeEnum.home:
                return baseValidation;
            case TypeEnum.office:
                return baseValidation;
            case TypeEnum.other:
                return {
                    ...baseValidation,
                    address_label: addressLabel, // Required only for "Other"
                };
            default:
                return baseValidation;
        }
    };
    const { errors, isValid } = validateForm(formToValidate(addressType));
    // Populate the form if editing an address.
    useEffect(() => {
        if (addressToEdit !== null) {
            setNewAddress({
                fullAddress: addressToEdit?.full_address ?? "",
                coordinates: {
                    lat: addressToEdit?.latitude.toString() ?? "",
                    lng: addressToEdit?.longitude.toString() ?? "",
                },
            });
            // Check if label is one of the standard types, otherwise it's "Other" with custom label
            const editLabel = addressToEdit?.label ?? "";
            const isStandardType = Object.values(TypeEnum).includes(editLabel);
            if (isStandardType) {
                setAddressType(editLabel);
                setAddressLabel("");
            }
            else {
                setAddressType(TypeEnum.other);
                setAddressLabel(editLabel);
            }
            setInstructions(addressToEdit?.notes ?? "");
            setIsDefault(addressToEdit?.default ?? false);
            setHouseNumber(addressToEdit?.apartment_number ?? "");
            setSuiteFloor(addressToEdit?.suite_floor ?? "");
            setUnitNumber(addressToEdit?.unit_number ?? "");
            setBusinessName(addressToEdit?.business_name ?? "");
            setBuildingName(addressToEdit?.complex_building_details ?? "");
        }
    }, [addressToEdit]);
    useEffect(() => {
        if (newAddress) {
            setCoordinates({
                lat: Number(newAddress.coordinates.lat),
                lng: Number(newAddress.coordinates.lng),
            });
        }
    }, [newAddress]);
    useEffect(() => {
        if (!placeId)
            return;
        fetch(`${CLIENT_API_URL}/v2/google/placeiddetails?place_id=${placeId}`, {
            method: "GET",
        })
            .then((response) => response.json())
            .then((data) => {
            if (data.success) {
                const address = buildAddressObject(data.content);
                setNewAddress(address);
            }
            else {
                // console.log("Address Select ERROR", data.message);
            }
        });
    }, [placeId]);
    useEffect(() => {
        clearInputs();
    }, [addressType]);
    const onChipClick = (value) => {
        setAddressType(value);
    };
    const clearInputs = () => {
        setSuiteFloor("");
        setUnitNumber("");
        setBusinessName("");
        setBuildingName("");
        setHouseNumber("");
    };
    const onSubmit = () => {
        setIsLoading(true);
        // Use custom label for "Other", otherwise use the type as the label
        const finalLabel = addressType === TypeEnum.other ? addressLabel : addressType;
        // Build address payload - include optional fields for "Other" type
        const addressPayload = {
            label: finalLabel,
            full_address: newAddress?.fullAddress ?? "",
            latitude: newAddress?.coordinates.lat ?? "",
            longitude: newAddress?.coordinates.lng ?? "",
            default: isDefault,
            notes: instructions,
        };
        // Add optional fields only for "Other" type (if provided)
        if (addressType === TypeEnum.other) {
            if (houseNumber)
                addressPayload.apartment_number = houseNumber;
            if (suiteFloor)
                addressPayload.suite_floor = suiteFloor;
            if (businessName)
                addressPayload.business_name = businessName;
            if (buildingName)
                addressPayload.complex_building_details = buildingName;
        }
        add_user_address(addressPayload).then((response) => {
            setIsLoading(false);
            if (response.success) {
                onComplete && onComplete();
            }
            else {
                dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
            }
        });
    };
    const RequiredLabel = (props) => (React.createElement("div", { className: "flex flex-row items-start gap-1" },
        React.createElement(LabelControl, { label: props.label }),
        React.createElement("div", { className: "text-error text-sm" }, "*")));
    return (React.createElement("div", { className: "flex flex-col gap-4 w-full max-h-[80vh]" },
        React.createElement("div", { className: "h-[200px] rounded-lg overflow-hidden flex-shrink-0" },
            React.createElement(MapControl, { controlSize: "tiny", zoom: coordinates.lat !== 0 && coordinates.lng !== 0 && newAddress?.fullAddress ? 15 : 8, markers: [{ position: coordinates, info: newAddress?.fullAddress }], center: { lat: coordinates.lat, lng: coordinates.lng } })),
        React.createElement("div", { className: "flex flex-col gap-4 overflow-y-auto flex-1" },
            React.createElement(AddressSearch, { onSelection: setPlaceId, showSubmitButton: false }),
            newAddress?.fullAddress && (React.createElement("div", { className: "flex items-center gap-2 p-3 bg-green/10 border border-green rounded-lg" },
                React.createElement(Icon, { icon: "lucide:check-circle", width: "20", height: "20", className: "text-green" }),
                React.createElement("span", { className: "text-sm" }, newAddress.fullAddress))),
            React.createElement("div", { className: "flex flex-col gap-5" },
                React.createElement("div", { className: "flex flex-col" },
                    React.createElement("div", { className: "flex flex-row items-start gap-1" },
                        React.createElement(LabelControl, { label: "Select Address Type", className: "mb-2" }),
                        React.createElement("div", { className: "text-error text-sm" }, "*")),
                    React.createElement("div", { className: "flex gap-2 flex-wrap" }, ADDRESS_TYPES.map((item) => (React.createElement("button", { key: item.label, disabled: addressToEdit ? true : false, className: `border-1 border-bg-light-blue rounded-xl py-2 px-3 ${addressToEdit ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:opacity-80"} ${addressType === item.label ? "bg-primary text-white border-primary" : "bg-light-blue text-primary border-gray"}`, onClick: () => onChipClick(item.label) },
                        React.createElement("div", { className: "flex justify-center gap-2" },
                            React.createElement(Icon, { icon: item.icon, width: "20", height: "20" }),
                            React.createElement("div", { className: "my-auto" }, item.label))))))),
                addressType === TypeEnum.other && (React.createElement(React.Fragment, null,
                    React.createElement(Input, { value: addressLabel, className: "", placeholder: "Home, Office, School, etc", type: "text", variant: "bordered", radius: "sm", label: React.createElement(RequiredLabel, { label: "Address Label" }), labelPlacement: "outside", size: "lg", maxLength: 50, onValueChange: setAddressLabel }),
                    React.createElement(Input, { value: houseNumber ?? "", className: "", placeholder: "6", type: "text", variant: "bordered", radius: "sm", label: "House / Unit Number (Optional)", labelPlacement: "outside", size: "lg", maxLength: 8, onValueChange: (value) => {
                            if (value) {
                                if (isNumberValue(value))
                                    setHouseNumber(value);
                            }
                            else
                                setHouseNumber(value);
                        } }),
                    React.createElement(Input, { value: suiteFloor ?? "", className: "", placeholder: "Floor 3, Suite 5", type: "text", variant: "bordered", radius: "sm", label: "Suite / Floor (Optional)", labelPlacement: "outside", size: "lg", maxLength: 20, onValueChange: setSuiteFloor }),
                    React.createElement(Input, { value: businessName ?? "", className: "", placeholder: "Acme Corp", type: "text", variant: "bordered", radius: "sm", label: "Business Name (Optional)", labelPlacement: "outside", size: "lg", maxLength: 80, onValueChange: setBusinessName }),
                    React.createElement(Input, { value: buildingName ?? "", className: "", placeholder: "The Oaks Complex", type: "text", variant: "bordered", radius: "sm", label: "Building / Complex Name (Optional)", labelPlacement: "outside", size: "lg", maxLength: 80, onValueChange: setBuildingName }))),
                React.createElement(Input, { value: instructions ?? "", className: "", placeholder: "Write instructions for the courier", type: "text", variant: "bordered", radius: "sm", label: "Address Instructions", labelPlacement: "outside", name: "instructions", size: "lg", onValueChange: setInstructions }),
                React.createElement(Checkbox, { color: "success", isSelected: isDefault, onValueChange: setIsDefault }, "Use this as your default address"),
                React.createElement("div", { className: "flex justify-center mt-3" },
                    React.createElement(Button, { color: "primary", size: "lg", fullWidth: true, radius: "md", isDisabled: !isValid, isLoading: isLoading, onPress: onSubmit, startContent: React.createElement(Icon, { icon: "lucide:plus", width: "20", height: "20" }) }, "Save Address"))))));
};
